import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { PROJECT_HEADER } from '@crm/shared';

export const baseURL = `${(import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? ''}/api`;

export const api = axios.create({ baseURL, withCredentials: true });

/** Public address of a stored image (contact photo, company logo). */
export const fileUrl = (id: string | null | undefined) => (id ? `${baseURL}/files/${id}` : undefined);

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export const tokenStore = {
  get: () => accessToken,
  set: (t: string | null) => {
    accessToken = t;
  },
  setUnauthorizedHandler: (fn: (() => void) | null) => {
    onUnauthorized = fn;
  },
};

/** Project picked in the header selector; sent with every request so lists narrow to it (null = all projects). */
let projectId: string | null = null;
export const projectStore = {
  get: () => projectId,
  set: (id: string | null) => {
    projectId = id;
  },
};

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  if (projectId) config.headers[PROJECT_HEADER] = projectId;
  return config;
});

let refreshing: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshing) {
    refreshing = axios
      .post<{ accessToken: string }>(`${baseURL}/auth/refresh`, null, { withCredentials: true })
      .then((r) => {
        accessToken = r.data.accessToken;
        return accessToken;
      })
      .catch(() => {
        accessToken = null;
        return null;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const config = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const url = config?.url ?? '';
    if (error.response?.status === 401 && config && !config._retried && !url.includes('/auth/')) {
      config._retried = true;
      const token = await refreshAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        return api.request(config);
      }
      onUnauthorized?.();
    }
    return Promise.reject(error);
  },
);

export interface ApiError {
  statusCode: number;
  message: string;
  errors?: { path: string; message: string }[];
}

export function errorMessage(e: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as ApiError | undefined;
    if (data?.errors?.length) return data.errors.map((x) => `${x.path}: ${x.message}`).join('; ');
    if (typeof data?.message === 'string') return data.message;
    if (Array.isArray(data?.message)) return (data.message as string[]).join('; ');
    return e.message || fallback;
  }
  return (e as Error)?.message ?? fallback;
}
